import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, Trash2, Minus, Plus, Send, ClipboardList, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useQuoteCart } from '../context/QuoteCartContext';
import api, { apiErrorMessage } from '../lib/api';

export default function QuoteCart() {
  const { items, count, open, setOpen, removeItem, setQty, clear } = useQuoteCart();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post('/submissions', {
        type: 'enquiry',
        name: form.name,
        email: form.email,
        phone: form.phone,
        items: items.map((i) => ({ productId: i.productId, name: i.name, sku: i.sku, qty: i.qty })),
      });
      toast({
        title: 'Enquiry sent!',
        description: data.email_sent
          ? `Your enquiry for ${items.length} item(s) was emailed to our team. We'll respond within 24 hours.`
          : `We received your enquiry for ${items.length} item(s). Our team will respond within 24 hours.`,
      });
      clear();
      setForm({ name: '', email: '', phone: '' });
      setOpen(false);
    } catch (err) {
      toast({ title: 'Could not send enquiry', description: apiErrorMessage(err), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(true)} data-testid="enquiry-fab" className="fixed bottom-6 right-6 z-[80] group flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-accent text-white pl-4 pr-5 py-3 shadow-xl shadow-primary/40 hover:opacity-95 transition-opacity">
        <span className="relative">
          <ShoppingCart className="w-5 h-5" />
          {count > 0 && <span className="absolute -top-2 -right-2 grid place-items-center w-5 h-5 rounded-full bg-white text-primary text-[11px] font-bold">{count}</span>}
        </span>
        <span className="font-heading font-bold text-sm">Enquiry</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.aside className="fixed top-0 right-0 z-[95] h-full w-full max-w-md bg-[#0a0e18] border-l border-white/10 flex flex-col"
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 30, stiffness: 300 }}>
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-bold text-lg text-white">Enquiry List</h3>
                  <span className="text-xs text-gray-500">({count})</span>
                </div>
                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
                {items.length === 0 ? (
                  <div className="h-full grid place-items-center text-center">
                    <div>
                      <ShoppingCart className="w-10 h-10 text-gray-600 mx-auto" />
                      <p className="text-gray-400 mt-3 font-heading font-semibold">Your enquiry list is empty</p>
                      <p className="text-sm text-gray-600 mt-1">Add products and SKUs to request a quote.</p>
                    </div>
                  </div>
                ) : (
                  items.map((i) => (
                    <div key={`${i.productId}-${i.sku}`} className="flex gap-3 glass rounded-2xl p-3">
                      <img src={i.image} alt={i.name} className="w-16 h-16 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading font-semibold text-white text-sm truncate">{i.name}</h4>
                        {i.sku && <span className="text-xs fire-text font-semibold">{i.sku}</span>}
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => setQty(i.productId, i.sku, i.qty - 1)} className="grid place-items-center w-6 h-6 rounded-md bg-white/5 text-white hover:bg-white/10"><Minus className="w-3 h-3" /></button>
                          <span className="text-sm text-white font-semibold w-6 text-center">{i.qty}</span>
                          <button onClick={() => setQty(i.productId, i.sku, i.qty + 1)} className="grid place-items-center w-6 h-6 rounded-md bg-white/5 text-white hover:bg-white/10"><Plus className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <button onClick={() => removeItem(i.productId, i.sku)} className="text-gray-500 hover:text-primary self-start"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))
                )}
              </div>

              {items.length > 0 && (
                <form onSubmit={send} data-testid="enquiry-form" className="border-t border-white/10 p-6 space-y-3">
                  <Input name="name" data-testid="enquiry-name" value={form.name} onChange={change} required placeholder="Your name" className="h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input name="email" data-testid="enquiry-email" type="email" value={form.email} onChange={change} required placeholder="Email" className="h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                    <Input name="phone" data-testid="enquiry-phone" value={form.phone} onChange={change} required placeholder="Phone" className="h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={clear} className="bg-white/5 border-white/15 text-white hover:bg-white/10">Clear</Button>
                    <Button type="submit" data-testid="enquiry-submit" disabled={sending} className="flex-1 gap-2 bg-gradient-to-r from-primary to-accent hover:opacity-90">
                      {sending ? 'Sending…' : <>Send Enquiry <Send className="w-4 h-4" /></>}
                    </Button>
                  </div>
                </form>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
